import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import './NotificationsPage.css';
import './FormModal.css';

const MSK_OFFSET = 3 * 60 * 60 * 1000;
const getToken = () => localStorage.getItem('token');

function toMskDate(date) {
  return new Date(date.getTime() + MSK_OFFSET);
}

const MONTHS_RU = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

function formatLetterDate(sentAt) {
  const date = new Date(sentAt);
  const msk = toMskDate(date);
  const d = String(msk.getUTCDate()).padStart(2, '0');
  const m = String(msk.getUTCMonth() + 1).padStart(2, '0');
  const y = msk.getUTCFullYear();
  const h = String(msk.getUTCHours()).padStart(2, '0');
  const min = String(msk.getUTCMinutes()).padStart(2, '0');
  return `${d}.${m}.${y}, ${h}:${min} (МСК)`;
}

function formatLetterDateLong(sentAt) {
  const date = new Date(sentAt);
  const msk = toMskDate(date);
  const day = msk.getUTCDate();
  const month = MONTHS_RU[msk.getUTCMonth()];
  const year = msk.getUTCFullYear();
  const h = String(msk.getUTCHours()).padStart(2, '0');
  const min = String(msk.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} ${h}:${min}`;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

const NotificationsPage = ({ asModal, onClose }) => {
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canSend, setCanSend] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, letterId: null });
  const [deleteError, setDeleteError] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [composeSuccess, setComposeSuccess] = useState('');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const SUBJECT_MAX_LENGTH = 500;
  const [pushSupported, setPushSupported] = useState(false);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushSubscribing, setPushSubscribing] = useState(false);
  const [pushError, setPushError] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);

  const [recipientType, setRecipientType] = useState('all');
  const [targetUserId, setTargetUserId] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [showUsersDropdown, setShowUsersDropdown] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const fileInputRef = React.useRef(null);
  const usersDropdownRef = React.useRef(null);

  useEffect(() => {
    const ok = typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(!!ok);
    if (ok) {
      setPushStatus(Notification.permission);
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const enablePush = async () => {
    if (!pushSupported || !getToken()) return;
    setPushError('');
    setPushSubscribing(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPushStatus(perm);
        setPushError(perm === 'denied' ? 'Разрешение на уведомления отклонено' : '');
        setPushSubscribing(false);
        return;
      }
      setPushStatus('granted');
      const vapidRes = await fetch('/api/notifications/vapid-public');
      if (!vapidRes.ok) {
        const d = await vapidRes.json().catch(() => ({}));
        throw new Error(d.error || 'Сервер не поддерживает push');
      }
      const { publicKey } = await vapidRes.json();
      const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      await reg.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const token = getToken();
      const saveRes = await fetch('/api/notifications/push-subscribe', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      if (!saveRes.ok) throw new Error('Не удалось сохранить подписку');
      setPushStatus('granted');
      setPushEnabled(true);
    } catch (e) {
      setPushError(e.message || 'Не удалось включить уведомления');
    } finally {
      setPushSubscribing(false);
    }
  };

  useEffect(() => {
    if (!pushSupported || pushStatus !== 'granted' || !getToken()) return;
    fetch('/api/notifications/vapid-public')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data?.publicKey) return;
        navigator.serviceWorker.ready.then((reg) => {
          reg.pushManager.getSubscription().then((sub) => {
            if (sub) return;
            reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(data.publicKey),
            }).then((newSub) => {
              const token = getToken();
              if (!token) return;
              fetch('/api/notifications/push-subscribe', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ subscription: newSub.toJSON() }),
              }).catch(() => {});
            }).catch(() => {});
          });
        }).catch(() => {});
      })
      .catch(() => {});
  }, [pushSupported, pushStatus]);

  useEffect(() => {
    const t = getToken();
    if (!t) {
      setError('Нужна авторизация');
      setLoading(false);
      return;
    }
    Promise.all([
      fetch('/api/notifications/letters', { headers: { Authorization: `Bearer ${t}` } })
        .then((res) => {
          if (!res.ok) throw new Error(res.status === 401 ? 'Нужна авторизация' : 'Ошибка загрузки');
          return res.json();
        })
        .then((data) => {
          const list = Array.isArray(data) ? data : [];
          setLetters(list);
          if (list.length > 0) {
            const maxId = Math.max(...list.map((l) => l.id));
            localStorage.setItem('notification_last_seen_id', String(maxId));
          }
        }),
      fetch('/api/notifications/can-send', { headers: { Authorization: `Bearer ${t}` } })
        .then((res) => (res.ok ? res.json() : { canSend: false }))
        .then((data) => setCanSend(data.canSend === true))
        .catch(() => setCanSend(false)),
      fetch('/api/notifications/can-delete', { headers: { Authorization: `Bearer ${t}` } })
        .then((res) => (res.ok ? res.json() : { canDelete: false }))
        .then((data) => setCanDelete(data.canDelete === true))
        .catch(() => setCanDelete(false)),
    ])
      .catch((err) => setError(err.message || 'Ошибка загрузки писем'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!canSend || recipientType !== 'personal') return;
    if (usersList.length > 0) return;
    const t = getToken();
    if (!t) return;
    setUsersLoading(true);
    fetch('/api/notifications/users-list', { headers: { Authorization: `Bearer ${t}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setUsersList(Array.isArray(data) ? data : []))
      .catch(() => setUsersList([]))
      .finally(() => setUsersLoading(false));
  }, [canSend, recipientType, usersList.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (usersDropdownRef.current && !usersDropdownRef.current.contains(e.target)) {
        setShowUsersDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setComposeError('Размер файла не должен превышать 5 МБ');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredUsers = usersList.filter((u) => {
    if (!usersSearch.trim()) return true;
    const q = usersSearch.toLowerCase();
    return (u.label || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const selectedUser = usersList.find((u) => String(u.id) === String(targetUserId));

  const handleSendBroadcast = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setComposeError('');
    setComposeSuccess('');
    if (!composeSubject.trim()) {
      setComposeError('Укажите тему сообщения');
      return;
    }
    if (!composeBody.trim()) {
      setComposeError('Укажите текст сообщения');
      return;
    }
    if (recipientType === 'personal' && !targetUserId) {
      setComposeError('Выберите получателя');
      return;
    }

    const t = getToken();
    if (!t) {
      setComposeError('Нужна авторизация');
      return;
    }

    setComposeSending(true);

    const formData = new FormData();
    formData.append('subject', composeSubject.trim());
    formData.append('body', composeBody.trim());
    formData.append('targetUserId', recipientType === 'personal' ? targetUserId : 'all');
    if (imageFile) formData.append('image', imageFile);

    fetch('/api/notifications/letters', {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}` },
      body: formData,
    })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .then((d) => Promise.reject(new Error(d.error || 'Ошибка отправки')))
            .catch(() => Promise.reject(new Error('Ошибка отправки')));
        }
        return res.json();
      })
      .then((newLetter) => {
        setLetters((prev) => [newLetter, ...prev]);
        setComposeSubject('');
        setComposeBody('');
        setRecipientType('all');
        setTargetUserId('');
        removeImage();
        setShowCompose(false);
        setComposeSuccess('Рассылка отправлена.');
        setTimeout(() => setComposeSuccess(''), 4000);
      })
      .catch((err) => setComposeError(err.message || 'Ошибка отправки'))
      .finally(() => setComposeSending(false));
  };

  const handleComposeCancel = () => {
    setComposeSubject('');
    setComposeBody('');
    setComposeError('');
    setComposeSuccess('');
    setRecipientType('all');
    setTargetUserId('');
    removeImage();
    setShowCompose(false);
  };

  const openDeleteConfirm = (letterId) => {
    if (!canDelete || deletingId) return;
    setDeleteError('');
    setDeleteConfirm({ isOpen: true, letterId });
  };

  const closeDeleteConfirm = () => setDeleteConfirm({ isOpen: false, letterId: null });

  const handleDeleteLetterConfirm = () => {
    const letterId = deleteConfirm.letterId;
    if (!letterId) {
      closeDeleteConfirm();
      return;
    }
    const t = getToken();
    if (!t) {
      closeDeleteConfirm();
      return;
    }
    setDeletingId(letterId);
    closeDeleteConfirm();
    fetch(`/api/notifications/letters/${letterId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${t}` },
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(new Error(d.error || 'Ошибка удаления')));
        return res.json();
      })
      .then(() => {
        setLetters((prev) => prev.filter((l) => l.id !== letterId));
        if (selectedLetter && selectedLetter.id === letterId) setSelectedLetter(null);
        setDeleteError('');
      })
      .catch((err) => setDeleteError(err.message || 'Ошибка удаления'))
      .finally(() => setDeletingId(null));
  };

  const title = 'Уведомления';

  const getBadgeLabel = (letter) => {
    if (letter.type === 'daily_work') return 'Сводка';
    if (letter.recipientType === 'personal') return 'Личное';
    if (letter.recipientType === 'broadcast') return 'Рассылка';
    return 'Системное';
  };
  const getBadgeClass = (letter) => {
    if (letter.type === 'daily_work') return 'daily-work';
    if (letter.recipientType === 'personal') return 'personal-type';
    if (letter.recipientType === 'broadcast') return 'broadcast';
    return 'auto';
  };

  const renderComposeForm = () => (
    <div className="npop-compose-wrap">
      <section className="mail-compose-card mail-compose-card-modal" aria-labelledby="compose-title">
        <form className="mail-compose-form" onSubmit={handleSendBroadcast} noValidate>
          <div className="mail-compose-card-header">
            <div className="mail-compose-title-row">
              <svg className="mail-compose-title-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              <h2 id="compose-title">Новая рассылка</h2>
            </div>
          </div>
          <div className="mail-compose-card-body">
            <div className="mail-compose-recipient-section">
              <label className="mail-compose-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                Получатели
              </label>
              <div className="mail-compose-recipient-toggle">
                <button type="button" className={`mail-recipient-btn${recipientType === 'all' ? ' active' : ''}`} onClick={() => { setRecipientType('all'); setTargetUserId(''); }} disabled={composeSending}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  Все
                </button>
                <button type="button" className={`mail-recipient-btn${recipientType === 'personal' ? ' active' : ''}`} onClick={() => setRecipientType('personal')} disabled={composeSending}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  Конкретный
                </button>
              </div>
              {recipientType === 'personal' && (
                <div className="mail-compose-user-picker" ref={usersDropdownRef}>
                  <div className="mail-user-picker-input-wrap">
                    <svg className="mail-user-picker-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input type="text" className="mail-user-picker-input" value={selectedUser ? selectedUser.label : usersSearch} onChange={(e) => { setUsersSearch(e.target.value); setTargetUserId(''); setShowUsersDropdown(true); }} onFocus={() => setShowUsersDropdown(true)} placeholder={usersLoading ? 'Загрузка...' : 'Поиск...'} disabled={composeSending || usersLoading} autoComplete="off" />
                    {(targetUserId || usersSearch) && (
                      <button type="button" className="mail-user-picker-clear" onClick={() => { setTargetUserId(''); setUsersSearch(''); setShowUsersDropdown(false); }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    )}
                  </div>
                  {showUsersDropdown && !usersLoading && (
                    <div className="mail-user-picker-dropdown">
                      {filteredUsers.length === 0 ? (
                        <div className="mail-user-picker-empty">Ничего не найдено</div>
                      ) : filteredUsers.slice(0, 50).map((u) => (
                        <button key={u.id} type="button" className={`mail-user-picker-option${String(u.id) === String(targetUserId) ? ' selected' : ''}`} onClick={() => { setTargetUserId(String(u.id)); setUsersSearch(''); setShowUsersDropdown(false); }}>
                          <span className="mail-user-picker-avatar">{(u.label || '?')[0].toUpperCase()}</span>
                          <span className="mail-user-picker-name">{u.label}</span>
                          {u.discord_username && u.email && <span className="mail-user-picker-email">{u.email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mail-compose-field">
              <label className="mail-compose-label" htmlFor="compose-subject-modal">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                Тема
              </label>
              <input id="compose-subject-modal" type="text" className="mail-compose-input" value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Тема сообщения" maxLength={SUBJECT_MAX_LENGTH} disabled={composeSending} autoComplete="off" />
              <span className="mail-compose-counter">{composeSubject.length}/{SUBJECT_MAX_LENGTH}</span>
            </div>
            <div className="mail-compose-field">
              <label className="mail-compose-label" htmlFor="compose-body-modal">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
                Текст
              </label>
              <textarea id="compose-body-modal" className="mail-compose-textarea" value={composeBody} onChange={(e) => setComposeBody(e.target.value)} placeholder="Текст рассылки..." rows={4} disabled={composeSending} />
            </div>
            <div className="mail-compose-image-section">
              <label className="mail-compose-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                Фото
              </label>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="mail-compose-file-input-hidden" onChange={handleImageSelect} disabled={composeSending} />
              {!imagePreview ? (
                <button type="button" className="mail-compose-image-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={composeSending}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span>Прикрепить фото</span>
                  <span className="mail-compose-image-hint">до 5 МБ</span>
                </button>
              ) : (
                <div className="mail-compose-image-preview-wrap">
                  <img src={imagePreview} alt="Превью" className="mail-compose-image-preview" />
                  <button type="button" className="mail-compose-image-remove" onClick={removeImage} title="Убрать">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                  <span className="mail-compose-image-name">{imageFile?.name}</span>
                </div>
              )}
            </div>
            {composeError && <div className="mail-compose-error" role="alert">{composeError}</div>}
            {composeSuccess && <div className="mail-compose-success" role="status">{composeSuccess}</div>}
          </div>
          <div className="mail-compose-actions">
            <button type="button" className="mail-compose-cancel" onClick={handleComposeCancel} disabled={composeSending}>Отмена</button>
            <button type="submit" className="mail-compose-send" disabled={composeSending}>
              {composeSending ? (<><svg className="mail-compose-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> Отправка...</>) : (<><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Отправить</>)}
            </button>
          </div>
        </form>
      </section>
    </div>
  );

  const renderDetailView = (letter) => (
    <div className="npop-detail">
      <button type="button" className="npop-detail-back" onClick={() => setSelectedLetter(null)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
        </svg>
        Назад к списку
      </button>
      <article className="npop-detail-card">
        <div className="npop-detail-header">
          <h3 className="npop-detail-title">{letter.subject}</h3>
          <div className="npop-detail-meta">
            <span className="npop-detail-date">{formatLetterDateLong(letter.sentAt)}</span>
            <span className={`notification-card-badge ${getBadgeClass(letter)}`}>{getBadgeLabel(letter)}</span>
            {letter.recipientType === 'personal' && letter.targetUsername && (
              <span className="notification-card-badge personal">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {letter.targetUsername}
              </span>
            )}
          </div>
        </div>
        <div className="npop-detail-body">
          {String(letter.body || '').split('\n\n').map((p, idx) => (<p key={idx}>{p}</p>))}
        </div>
        {letter.imageUrl && (
          <div className="npop-detail-image-wrap">
            <img src={letter.imageUrl} alt="" className="npop-detail-image npop-detail-image-clickable" loading="lazy" onClick={() => setLightboxUrl(letter.imageUrl)} />
          </div>
        )}
        {canDelete && (
          <div className="npop-detail-actions">
            <button type="button" className="npop-detail-delete-btn" onClick={() => openDeleteConfirm(letter.id)} disabled={deletingId === letter.id}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Удалить
            </button>
          </div>
        )}
      </article>
    </div>
  );

  if (asModal) {
    const composeOpen = canSend && showCompose;

    return (
      <>
        <div className="npop-header">
          <div className="npop-header-left">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
            </svg>
            <h3 className="npop-title">{title}</h3>
          </div>
          <div className="npop-header-right">
            {canSend && (
              <button type="button" className={`npop-compose-btn${composeOpen ? ' active' : ''}`} onClick={() => { setShowCompose((s) => !s); setSelectedLetter(null); }}>
                {composeOpen ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                )}
              </button>
            )}
            <button type="button" className="npop-close-btn" onClick={onClose} title="Закрыть">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        <div className="npop-body">
          {loading ? (
            <div className="npop-status">Загрузка...</div>
          ) : error ? (
            <div className="npop-status npop-status-error">{error}</div>
          ) : composeOpen ? (
            renderComposeForm()
          ) : selectedLetter ? (
            renderDetailView(selectedLetter)
          ) : (
            <>
              {pushSupported && !pushEnabled && pushStatus === 'default' && (
                <div className="npop-push-banner">
                  <span>Включить пуш-уведомления?</span>
                  <button type="button" className="npop-push-btn" onClick={enablePush} disabled={pushSubscribing}>
                    {pushSubscribing ? '...' : 'Включить'}
                  </button>
                </div>
              )}
              {composeSuccess && <div className="mail-compose-success" role="status" style={{ margin: '0 0 8px' }}>{composeSuccess}</div>}
              {deleteError && <div className="mail-compose-error" role="alert" style={{ margin: '0 0 8px' }}>{deleteError}</div>}
              {letters.length === 0 ? (
                <div className="npop-empty">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                  <span>Нет уведомлений</span>
                </div>
              ) : (
                <div className="npop-list">
                  {letters.map((letter) => (
                    <button key={letter.id} type="button" className="npop-item" onClick={() => setSelectedLetter(letter)}>
                      <div className="npop-item-left">
                        <div className={`npop-item-dot ${getBadgeClass(letter)}`} />
                      </div>
                      <div className="npop-item-content">
                        <span className="npop-item-subject">{letter.subject}</span>
                        <span className="npop-item-preview">{(letter.body || '').slice(0, 80)}{(letter.body || '').length > 80 ? '...' : ''}</span>
                        <div className="npop-item-meta">
                          <span className="npop-item-date">{formatLetterDateLong(letter.sentAt)}</span>
                          <span className={`npop-item-badge ${getBadgeClass(letter)}`}>{getBadgeLabel(letter)}</span>
                          {letter.imageUrl && (
                            <svg className="npop-item-has-image" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                          )}
                        </div>
                      </div>
                      <svg className="npop-item-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <ConfirmModal
          isOpen={deleteConfirm.isOpen}
          onClose={closeDeleteConfirm}
          onConfirm={handleDeleteLetterConfirm}
          title="Удалить уведомление"
          message="Удалить это уведомление? Это действие нельзя отменить."
        />

        {lightboxUrl && (
          <div className="npop-lightbox" onClick={() => setLightboxUrl(null)}>
            <button type="button" className="npop-lightbox-close" onClick={() => setLightboxUrl(null)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <img src={lightboxUrl} alt="" className="npop-lightbox-img" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </>
    );
  }

  if (loading) {
    return (
      <div className="notifications-page">
        <header className="mail-header">
          <button type="button" className="mail-back" onClick={() => navigate(-1)} title="Назад">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span>Назад</span>
          </button>
          <h1 className="mail-title">{title}</h1>
        </header>
        <div className="mail-loading">Загрузка...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notifications-page">
        <header className="mail-header">
          <button type="button" className="mail-back" onClick={() => navigate(-1)} title="Назад">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span>Назад</span>
          </button>
          <h1 className="mail-title">{title}</h1>
        </header>
        <div className="mail-error">{error}</div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <header className="mail-header">
        <button type="button" className="mail-back" onClick={() => navigate(-1)} title="Назад">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          <span>Назад</span>
        </button>
        <div className="mail-header-title-row">
          <h1 className="mail-title">{title}</h1>
          <span className="mail-inbox-label">Входящие</span>
        </div>
      </header>

      <main className="mail-main">
        {canSend && (
          <section className="mail-compose-card" aria-labelledby="compose-title-page">
            <form className="mail-compose-form" onSubmit={handleSendBroadcast} noValidate>
              <div className="mail-compose-card-header">
                <div className="mail-compose-title-row">
                  <svg className="mail-compose-title-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <h2 id="compose-title-page">Новая рассылка</h2>
                </div>
              </div>
              <div className="mail-compose-card-body">
                <div className="mail-compose-recipient-section">
                  <label className="mail-compose-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    Получатели
                  </label>
                  <div className="mail-compose-recipient-toggle">
                    <button type="button" className={`mail-recipient-btn${recipientType === 'all' ? ' active' : ''}`} onClick={() => { setRecipientType('all'); setTargetUserId(''); }} disabled={composeSending}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      Все пользователи
                    </button>
                    <button type="button" className={`mail-recipient-btn${recipientType === 'personal' ? ' active' : ''}`} onClick={() => setRecipientType('personal')} disabled={composeSending}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                      Конкретный человек
                    </button>
                  </div>
                  {recipientType === 'personal' && (
                    <div className="mail-compose-user-picker" ref={usersDropdownRef}>
                      <div className="mail-user-picker-input-wrap">
                        <svg className="mail-user-picker-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        <input type="text" className="mail-user-picker-input" value={selectedUser ? selectedUser.label : usersSearch} onChange={(e) => { setUsersSearch(e.target.value); setTargetUserId(''); setShowUsersDropdown(true); }} onFocus={() => setShowUsersDropdown(true)} placeholder={usersLoading ? 'Загрузка...' : 'Поиск по имени или email...'} disabled={composeSending || usersLoading} autoComplete="off" />
                        {(targetUserId || usersSearch) && (
                          <button type="button" className="mail-user-picker-clear" onClick={() => { setTargetUserId(''); setUsersSearch(''); setShowUsersDropdown(false); }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        )}
                      </div>
                      {showUsersDropdown && !usersLoading && (
                        <div className="mail-user-picker-dropdown">
                          {filteredUsers.length === 0 ? (
                            <div className="mail-user-picker-empty">Ничего не найдено</div>
                          ) : (
                            filteredUsers.slice(0, 50).map((u) => (
                              <button key={u.id} type="button" className={`mail-user-picker-option${String(u.id) === String(targetUserId) ? ' selected' : ''}`} onClick={() => { setTargetUserId(String(u.id)); setUsersSearch(''); setShowUsersDropdown(false); }}>
                                <span className="mail-user-picker-avatar">{(u.label || '?')[0].toUpperCase()}</span>
                                <span className="mail-user-picker-name">{u.label}</span>
                                {u.discord_username && u.email && <span className="mail-user-picker-email">{u.email}</span>}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mail-compose-field">
                  <label className="mail-compose-label" htmlFor="compose-subject-page">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
                    Тема
                  </label>
                  <input id="compose-subject-page" type="text" className="mail-compose-input" value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Тема сообщения" maxLength={SUBJECT_MAX_LENGTH} disabled={composeSending} autoComplete="off" />
                  <span className="mail-compose-counter">{composeSubject.length}/{SUBJECT_MAX_LENGTH}</span>
                </div>
                <div className="mail-compose-field">
                  <label className="mail-compose-label" htmlFor="compose-body-page">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="17" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="17" y1="18" x2="3" y2="18"/></svg>
                    Текст сообщения
                  </label>
                  <textarea id="compose-body-page" className="mail-compose-textarea" value={composeBody} onChange={(e) => setComposeBody(e.target.value)} placeholder="Текст рассылки..." rows={5} disabled={composeSending} />
                </div>

                <div className="mail-compose-image-section">
                  <label className="mail-compose-label">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                    Изображение (необязательно)
                  </label>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="mail-compose-file-input-hidden" onChange={handleImageSelect} disabled={composeSending} />
                  {!imagePreview ? (
                    <button type="button" className="mail-compose-image-upload-btn" onClick={() => fileInputRef.current?.click()} disabled={composeSending}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      <span>Прикрепить фото</span>
                      <span className="mail-compose-image-hint">JPG, PNG, GIF, WebP до 5 МБ</span>
                    </button>
                  ) : (
                    <div className="mail-compose-image-preview-wrap">
                      <img src={imagePreview} alt="Превью" className="mail-compose-image-preview" />
                      <button type="button" className="mail-compose-image-remove" onClick={removeImage} title="Убрать изображение">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                      <span className="mail-compose-image-name">{imageFile?.name}</span>
                    </div>
                  )}
                </div>

                {composeError && <div className="mail-compose-error" role="alert">{composeError}</div>}
                {composeSuccess && <div className="mail-compose-success" role="status">{composeSuccess}</div>}
              </div>
              <div className="mail-compose-actions">
                <button type="button" className="mail-compose-cancel" onClick={handleComposeCancel} disabled={composeSending}>Отмена</button>
                <button type="submit" className="mail-compose-send" disabled={composeSending}>
                  {composeSending ? (
                    <><svg className="mail-compose-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> Отправка...</>
                  ) : (
                    <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg> Отправить</>
                  )}
                </button>
              </div>
            </form>
          </section>
        )}

        {pushSupported && !pushEnabled && pushStatus === 'default' && (
          <section className="mail-push-card">
            <p className="mail-push-desc">Получать пуш-уведомления о новых рассылках на телефон (если приложение добавлено на главный экран — iOS/Android).</p>
            <button type="button" className="mail-compose-send" onClick={enablePush} disabled={pushSubscribing}>
              {pushSubscribing ? 'Включение...' : 'Включить уведомления'}
            </button>
            {pushError && <div className="mail-compose-error">{pushError}</div>}
          </section>
        )}

        <section className="mail-list-section">
          {deleteError && (
            <div className="mail-compose-error" role="alert">{deleteError}</div>
          )}
          {letters.length === 0 ? (
            <div className="mail-empty-card">
              <h2>Пока нет уведомлений</h2>
              <p>Автоматические напоминания о статистике появятся здесь по понедельникам и четвергам в 00:00 (МСК).</p>
            </div>
          ) : (
            <div className="notifications-cards-list">
              {letters.map((letter) => (
                <article key={letter.id} className="notification-card">
                  <h3 className="notification-card-title">{letter.subject}</h3>
                  <div className="notification-card-body">
                    {String(letter.body || '').split('\n\n').map((p, idx) => (<p key={idx}>{p}</p>))}
                  </div>
                  {letter.imageUrl && (
                    <div className="notification-card-image-wrap">
                      <img src={letter.imageUrl} alt="" className="notification-card-image npop-detail-image-clickable" loading="lazy" onClick={() => setLightboxUrl(letter.imageUrl)} />
                    </div>
                  )}
                  <div className="notification-card-footer">
                    <span className="notification-card-date">{formatLetterDateLong(letter.sentAt)}</span>
                    <div className="notification-card-footer-right">
                      {letter.recipientType === 'personal' && letter.targetUsername && (
                        <span className="notification-card-badge personal">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          {letter.targetUsername}
                        </span>
                      )}
                      {(letter.recipientType || letter.type) && (
                        <span className={`notification-card-badge ${letter.type === 'daily_work' ? 'daily-work' : letter.recipientType === 'personal' ? 'personal-type' : letter.recipientType === 'broadcast' ? 'broadcast' : 'auto'}`}>
                          {letter.type === 'daily_work' ? 'Сводка' : letter.recipientType === 'personal' ? 'Личное' : letter.recipientType === 'broadcast' ? 'Рассылка' : 'Системное'}
                        </span>
                      )}
                      {canDelete && (
                        <button type="button" className="notification-card-delete" onClick={() => openDeleteConfirm(letter.id)} disabled={deletingId === letter.id} title="Удалить уведомление" aria-label="Удалить уведомление">
                          {deletingId === letter.id ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83"/></svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        <ConfirmModal
          isOpen={deleteConfirm.isOpen}
          onClose={closeDeleteConfirm}
          onConfirm={handleDeleteLetterConfirm}
          title="Удалить уведомление"
          message="Удалить это уведомление? Это действие нельзя отменить."
        />
      </main>

      {lightboxUrl && (
        <div className="npop-lightbox" onClick={() => setLightboxUrl(null)}>
          <button type="button" className="npop-lightbox-close" onClick={() => setLightboxUrl(null)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <img src={lightboxUrl} alt="" className="npop-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
